import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

export default function Footer() {
  const { t } = useLanguage();

  return (
    <footer className="bg-white text-slate-700 pt-16 pb-8 border-t border-slate-100 relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          
          {/* Brand Col */}
          <div className="space-y-4">
            <Link to="/" className="inline-block">
              <img 
                src="https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=90,format=auto/assets/086b06dd-92e0-474f-8c57-ea0b72c8dd5a/screenshot-2026-08-06-at-14-45-09-removebg-preview.png" 
                alt="Captain Hurghada Tours" 
                className="h-16 w-auto object-contain drop-shadow-sm"
              />
            </Link>
            <div className="pt-2 flex flex-col gap-2.5">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 text-xs font-medium text-slate-700 w-fit bg-white">
                <i className="fa-solid fa-shield-halved text-cyan-600 text-xs" aria-hidden="true" />
                <span>{t('footer.danishSafety') || 'Dansk tryghed'}</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 text-xs font-medium text-slate-700 w-fit bg-white">
                <i className="fa-solid fa-compass text-cyan-600 text-xs" aria-hidden="true" />
                <span>{t('footer.localExp') || 'Lokal ekspertise'}</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900 mb-4">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-600 font-medium">
              <li>
                <Link to="/" className="hover:text-slate-900 transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/om-os" className="hover:text-slate-900 transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser" className="hover:text-slate-900 transition-colors">
                  {t('nav.experiences')}
                </Link>
              </li>
              <li>
                <Link to="/planlaeg-din-rejse" className="hover:text-slate-900 transition-colors">
                  {t('nav.planTrip')}
                </Link>
              </li>
              <li>
                <Link to="/faq" className="hover:text-slate-900 transition-colors">
                  {t('nav.faq')}
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-slate-900 transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Experience Subpages */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900 mb-4">
              {t('nav.experiences')}
            </h4>
            <ul className="space-y-2 text-xs text-slate-600 font-medium">
              <li>
                <Link to="/oplevelser/hestetur" className="hover:text-slate-900 transition-colors">
                  {t('nav.horse')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser/baadture" className="hover:text-slate-900 transition-colors">
                  {t('nav.boatTrips')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser/speedboat" className="hover:text-slate-900 transition-colors">
                  {t('nav.speedboat')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser/safari-tur" className="hover:text-slate-900 transition-colors">
                  {t('nav.safari')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser/supersafari-hurghada" className="hover:text-slate-900 transition-colors">
                  {t('nav.superSafari')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser/stor-baadtur" className="hover:text-slate-900 transition-colors">
                  {t('nav.bigBoat')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser/citytour-hurghada" className="hover:text-slate-900 transition-colors">
                  {t('nav.citytour')}
                </Link>
              </li>
              <li>
                <Link to="/oplevelser/private-ture" className="hover:text-slate-900 transition-colors">
                  {t('nav.privateTours')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Details */}
          <div>
            <h4 className="font-bold text-xs uppercase tracking-wider text-slate-900 mb-4">
              {t('footer.contactInfo')}
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-600 font-medium">
              <li className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-cyan-600 mt-0.5" aria-hidden="true" />
                <span>Hurghada, Rødehavet, Egypten</span>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-cyan-600" aria-hidden="true" />
                <a href="mailto:nicolknejensen@gmail.com" className="hover:text-slate-900 transition-colors">
                  nicolknejensen@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-brands fa-whatsapp text-cyan-600" aria-hidden="true" />
                <a href="tel:+201104354994" className="hover:text-slate-900 transition-colors">
                  +20 110 435 4994 (WhatsApp)
                </a>
              </li>
              <li className="flex items-center gap-3">
                <i className="fa-brands fa-instagram text-cyan-600" aria-hidden="true" />
                <a 
                  href="https://instagram.com/captainhurgada" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-slate-900 transition-colors font-semibold"
                >
                  @captainhurgada
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Sub-Footer */}
        <div className="pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <p>
            {t('footer.copyright')}
          </p>
          <a 
            href="https://captainhurghadatours.dk" 
            className="hover:text-slate-600 transition-colors"
          >
            captainhurghadatours.dk
          </a>
        </div>

      </div>
    </footer>
  );
}
